import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import isEmail from 'validator/es/lib/isEmail';
import isMobilePhone from 'validator/es/lib/isMobilePhone';
import * as Yup from 'yup';
import { appConfig } from 'config/app';
import { DEFAULT_BACKDATE_LIMIT, MOBILE_LOCALE } from 'config/const';
import { IJobTitleState } from 'store/types';
import { EmployeeRole, IEmployeeSite, IEmployerSite, ISite } from 'types';
import { formatDateForApi, isEmployeeIdRequired, isValidBusinessDate } from 'utils';

dayjs.extend(isSameOrBefore);
dayjs.extend(minMax);

const passwordRegex = /^(?=.*[0-9])(?=.*[A-z])(?=.*[\W_]).{8,}$/;
const nameRegex = /^[A-Za-zÀ-ùÁ-ú0-9-.' ]*$/;
const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;

const passwordRequirementsmessage = 'Password needs to have minimum 8 characters, with at least 1 each: letter, number, special character';
const firstNameRequirementsMessage = 'Your first name contains an invalid character';
const lastNameRequirementsMessage = 'Your last name contains an invalid character';

export const isValidDateOfBirth = (date: string): boolean => {
  const dateMatch = date.match(/^[12]\d{3}(-(0[1-9]|1[0-2])(-(0[1-9]|[12][0-9]|3[01])))$/);
  if (!dateMatch) return false;

  const birthDate = dayjs(date);
  if (!birthDate.isValid()) return false;

  if (birthDate.isAfter(dayjs())) return false; // validate that the date is not greater than today.
  if (birthDate.isBefore(dayjs().subtract(100, 'year'))) return false; // validate the date is not greater than 100 years ago.

  return true;
};

export function validateFormat(date: string, format: string) {
  return dayjs(date, format).format(format) === date;
}

const DOBValidationSchema = {
  dateOfBirth: Yup.string()
    .test('isValidDateOfBirth', 'Invalid date of birth', (dateOfBirth: string) => {
      if (validateFormat(dateOfBirth, 'MM-DD-YYYY')) {
        return isValidDateOfBirth(formatDateForApi(dateOfBirth));
      }
      return false;
    })
    .required(),
};

const RecoveryPinValidationSchema = {
  recoveryPin: Yup.string().required('A 4 digit code must be entered').min(4, 'A 4 digit code must be entered').max(4, 'A 4 digit code must be entered'),
};

const identityValidationSchema = {
  identity: Yup.string()
    .test('IsIdentity', 'A valid email address is required', (identity: string) => isEmail(identity || ''))
    .required(),
};

const firstNameValidationSchema = Yup.string().matches(nameRegex, firstNameRequirementsMessage).trim().required('First name is required').max(appConfig.NAME.MAX, `First Name must be at most ${appConfig.NAME.MAX} characters`);
const lastNameValidationSchema = Yup.string().matches(nameRegex, lastNameRequirementsMessage).trim().required('Last name is required').max(appConfig.NAME.MAX, `Last Name must be at most ${appConfig.NAME.MAX} characters`);
const employeeIdValidationSchema = (sites?: Array<ISite | IEmployerSite | IEmployeeSite>) =>
  Yup.string()
    .trim()
    .max(appConfig.EMPLOYEE_ID.MAX, `Employee ID must be ${appConfig.EMPLOYEE_ID.MAX} characters or less`)
    .test('IsRequired', 'Employee ID is required', (value: string, context: Yup.TestContext<any>) => {
      if (value || context.parent.role?.value === EmployeeRole.ADMIN || !context.parent.site?.value) return true;
      return !isEmployeeIdRequired(context.parent.site.value, sites);
    });

const timeValidationSchema = Yup.string().required('Required field').matches(timeRegex, 'Invalid time');

const verificationCodeValidationSchema = Yup.string().required('A 6 digit code must be entered').min(6, 'A 6 digit code must be entered').max(6, 'A 6 digit code must be entered');

const signUpGoogleValidationSchema = {
  firstName: firstNameValidationSchema,
  lastName: lastNameValidationSchema,
  termsOfService: Yup.bool().oneOf([true], 'You must accept this disclosure to continue'),
  privacyPolicy: Yup.bool().oneOf([true], 'You must accept the Privacy Policy to continue'),
  ...identityValidationSchema,
};

const passwordValidationSchema = Yup.string().max(32, 'Password must be at most 32 characters  ').matches(passwordRegex, passwordRequirementsmessage).min(8, 'Password must be at least 8 characters  ').required('Password is required');
const signUpValidationSchema = {
  ...signUpGoogleValidationSchema,
  password: passwordValidationSchema,
};

const mobileNumberValidationSchema = Yup.string()
  .required('A mobile number is required.')
  .test('isMobilePhone', 'Please enter a valid mobile number', (phoneNumber: string) => isMobilePhone(phoneNumber || '', MOBILE_LOCALE));

const signUpSecurityValidationSchema = {
  identity: mobileNumberValidationSchema,
  ...DOBValidationSchema,
  ...RecoveryPinValidationSchema,
};

const forgotPasswordValidationSchema = Yup.object().shape({
  ...identityValidationSchema,
  ...DOBValidationSchema,
});

const accountRecoveryValidationSchema = Yup.object().shape({
  ...RecoveryPinValidationSchema,
  ...DOBValidationSchema,
});

const accountRecoveryAddIdentityValidationSchema = Yup.object().shape({
  identity: mobileNumberValidationSchema,
});

const employeeSiteValidationSchema = (sites?: Array<ISite | IEmployerSite | IEmployeeSite>) => ({
  employeeId: employeeIdValidationSchema(sites),
  role: Yup.object({ label: Yup.string(), value: Yup.string().required() }).required('Role is required'),
  site: Yup.object().test({
    name: 'RequiredSiteIfEmployee',
    message: 'Site is required',
    test(site) {
      return this.parent.role?.value === EmployeeRole.ADMIN || !!site.value;
    },
  }),
  jobTitle: Yup.object({ label: Yup.string(), value: Yup.object({ title: Yup.string() }) }).test({
    name: 'RequiredJobIfEmployee',
    message: 'Job title is required',
    test(jobTitle) {
      return this.parent.role?.value === EmployeeRole.ADMIN || !!(jobTitle?.value as IJobTitleState)?.title;
    },
  }),
  disbursementMethod: Yup.object({ label: Yup.string(), value: Yup.string() }).test({
    name: 'RequiredDisbursementIfEmployee',
    message: 'Disbursement method is required',
    test(disbursement) {
      return this.parent.role?.value === EmployeeRole.ADMIN || !!disbursement?.value;
    },
  }),
});

const makePaymentValidationSchema = (perTipMax = appConfig.TIP.MAX, timezone?: string, backdateLimit = DEFAULT_BACKDATE_LIMIT) => {
  const payeeSchema = {
    tipAmount: Yup.number().typeError('Invalid tip amount'),
    holdbackAmount: Yup.number().typeError('Invalid holdback'),
    cash: Yup.number().typeError('Invalid cash'),
    job: Yup.object().shape({
      classification: Yup.string(),
      title: Yup.string(),
    }),
    note: Yup.string().max(appConfig.PAYEE_NOTE.MAX, `Note cannot be more than ${appConfig.PAYEE_NOTE.MAX} characters`),
  };

  return Yup.object().shape({
    payees: Yup.array()
      .of(
        Yup.object()
          .shape(payeeSchema)
          .test('allowedMaxAndMin', 'Invalid total', (values) => {
            const total = values.tipAmount + values.cash;
            if (perTipMax < total || appConfig.TIP.MIN > total) return false;
            return true;
          })
      )
      .min(1, 'Must select at least one payee'),
    description: Yup.string().max(100, 'Description cannot be more than 100 characters'),
    paymentDate: Yup.string()
      .test('paymentDate', 'Invalid business date', (date: string) => isValidBusinessDate(date, timezone, backdateLimit))
      .required(),
    authorizedAgreement: Yup.boolean().oneOf([true], 'You must accept the terms of agreement to proceed'),
  });
};

export default {
  passwordRegex,
  passwordRequirementsmessage,
  isValidDateOfBirth,
  validateFormat,
  signUpGoogleValidationSchema,
  signUpValidationSchema,
  signUpSecurityValidationSchema,
  forgotPasswordValidationSchema,
  DOBValidationSchema,
  firstNameValidationSchema,
  lastNameValidationSchema,
  employeeIdValidationSchema,
  passwordValidationSchema,
  timeValidationSchema,
  verificationCodeValidationSchema,
  accountRecoveryValidationSchema,
  accountRecoveryAddIdentityValidationSchema,
  employeeSiteValidationSchema,
  makePaymentValidationSchema,
};
